import { Autocomplete, Box, MenuItem } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetAllStorageWithCustomers } from 'src/api/storage'
import { AppTextField } from 'src/components/AppTextField'
import { CancelButton } from 'src/components/CancelButton'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { FlexSpinner } from 'src/components/Spinner'
import { IStorage } from 'src/generated-types/storage-types'
import { useZodForm } from 'src/hooks/zod-form'
import {
  GroupHeader,
  GroupItems,
  MainComponentSendFormProps,
} from 'src/screens/localities/positions/components/MainComponentSendModal'
import { z } from 'zod'

const EquipmentStorageSchema = z.object({
  storageId: z.number(),
})

const EquipmentSendStorage: React.FC<MainComponentSendFormProps> = ({
  onSubmit,
  onCancel,
  customerId,
  initialValues,
  renderMainComponentSelectComponent,
  storageId,
}) => {
  const { t } = useTranslation(['position', 'common'])

  const form = useZodForm(EquipmentStorageSchema, {
    defaultValues: { ...initialValues },
  })

  const { data, isLoading } = useGetAllStorageWithCustomers(customerId)
  const filteredStorages = useMemo(() => {
    return storageId != null ? data?.filter(s => s.id != storageId) : data
  }, [data])

  if (isLoading) return <FlexSpinner />

  return (
    <AppForm form={form} onSubmit={onSubmit} hasInitialValues={initialValues}>
      {renderMainComponentSelectComponent && renderMainComponentSelectComponent(form)}
      <Autocomplete
        onChange={(_, value) => {
          form.setValue('storageId', (value as IStorage).id)
        }}
        options={filteredStorages || []}
        groupBy={(option: any) =>
          `${option.customer?.name} ${option.customer?.organizationNr ? `(${option?.customer?.organizationNr})` : ''}`
        }
        renderGroup={params => (
          <li>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        autoHighlight
        getOptionLabel={o => o.name}
        renderOption={(props, o) => (
          <MenuItem {...props} key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        )}
        renderInput={params => (
          <AppTextField
            {...params}
            label={t('select-storage', { ns: 'main-component' })}
            inputProps={{
              ...params.inputProps,
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Box className="flex justify-end gap-4 pt-4">
        <FormSubmitButton createText={t('Send', { ns: 'common' })} />
        <CancelButton onClick={onCancel}>{t('cancel', { ns: 'common' })}</CancelButton>
      </Box>
    </AppForm>
  )
}

export default EquipmentSendStorage
